import { useEffect, useMemo, useState } from "react";
import AmenityIconRenderer from "@/components/tpr/AmenityIconRenderer/AmenityIconRenderer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { defaultButtonClasses, secondaryButtonClasses } from "@/constants/ui";
import { useMember } from "@/contexts/memberContext";
import { useLocationsQuery } from "@/hooks/useLocationsQuery";
import { useOccurrenceAmenities } from "@/hooks/useOccurrenceAmenities";
import { useUpdateBookingAmenitiesMutation } from "@/hooks/useUpdateBookingAmenitiesMutation";
import { BookingService } from "@/services/BookingService";
import type { Amenity } from "@/types/Amenity";
import type { Booking } from "@/types/Booking";
import { Loader2, Package } from "lucide-react";
import { endTimeToMinutes } from "@/utils/bookingTime";
import { canManageLocationCharges } from "@/utils/userPermissions";

interface ManageAmenitiesDialogProps {
  booking: Booking | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const ManageAmenitiesDialog = ({
  booking,
  isOpen,
  onOpenChange,
  onSuccess,
}: ManageAmenitiesDialogProps) => {
  const [selectedAmenityIds, setSelectedAmenityIds] = useState<string[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  const updateAmenitiesMutation = useUpdateBookingAmenitiesMutation();

  // Fetch location data to get bookable amenities
  const { data: locations } = useLocationsQuery({
    filters: booking?.locationId ? { locationId: booking.locationId } : undefined,
    enabled: !!booking?.locationId && isOpen,
  });

  const location = useMemo(() => {
    return locations?.find((loc) => loc.id === booking?.locationId);
  }, [locations, booking?.locationId]);

  // Get bookable amenities from location. Hidden amenities are withdrawn from service and must not
  // be offered — EXCEPT one already attached to this booking, which stays listed so it remains
  // visible and removable. Filtering those out would strand an amenity on the booking with no way
  // to see or detach it, which defeats the point of hiding (rather than deleting) in the first
  // place: preserving the integrity of bookings that already reference it.
  const bookableAmenities = useMemo(() => {
    const attachedIds = new Set(booking?.bookedAmenities?.map((ba) => ba.amenityId) ?? []);
    return location?.amenities?.filter((a) => a.isBookable && (!a.hidden || attachedIds.has(a.id))) || [];
  }, [location, booking?.bookedAmenities]);

  // Initialize selected amenities from booking when dialog opens
  useEffect(() => {
    if (isOpen && booking?.bookedAmenities) {
      const currentAmenityIds = booking.bookedAmenities.map((ba) => ba.amenityId);
      setSelectedAmenityIds(currentAmenityIds);
    } else if (isOpen) {
      setSelectedAmenityIds([]);
    }
  }, [isOpen, booking?.bookedAmenities]);

  // Fetch bookings for availability checking
  useEffect(() => {
    const fetchBookings = async () => {
      if (!booking?.locationId || !booking?.date || !isOpen) {
        setBookings([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const bookingService = new BookingService();
        const fetchedBookings = await bookingService.getBookingsForLocationAndDate(
          booking.locationId,
          new Date(booking.date)
        );
        setBookings(fetchedBookings);
      } catch (error) {
        console.error("Error fetching bookings:", error);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [booking?.locationId, booking?.date, isOpen]);

  // A recurring/slot booking's amenity is replaced for the WHOLE series, and `booking.date` is only
  // its genesis — so a per-date check here is wrong both ways (it greyed out a space busy only on the
  // genesis and passed one busy on later dates: the Cardiff CP14/WPI double-booking). For a series the
  // server is authoritative: PATCH /booking/:id/amenities checks every future occurrence and rejects
  // naming the clashing dates (surfaced by the mutation's error toast).
  const isSeries = !!booking && (!!booking.recurrence || !!booking.recurrencePattern || booking.type === "slot");

  // R27/D3 — a whole-series change keeps per-date customisations; tell staff how many. The GET is
  // admin / centre-PM only (location-scoped, as the charge controls), so nobody else calls it.
  const { member } = useMember();
  const { data: occurrenceAmenities } = useOccurrenceAmenities(
    booking?.id,
    isOpen && isSeries && canManageLocationCharges(member, booking?.locationId),
  );
  const customisedCount = occurrenceAmenities?.filter((o) => o.customised).length ?? 0;

  // Check if an amenity is available for the booking time.
  // R23/R6 — an amenity mirrors its parent booking's 15-min buffers, so a different member cannot
  // take the same amenity back-to-back (must clear the buffer); the SAME member may sit adjacent.
  // This mirrors the backend conflict check (AmenityBookingRepository.getAmenityBookingsForDateAndTime).
  const isAmenityAvailable = (amenity: Amenity): boolean => {
    if (!booking) return false;
    if (isSeries) return true;

    // Get IDs of amenities already booked by this booking (these don't count as conflicts)
    const currentBookingAmenityIds = new Set(
      booking.bookedAmenities?.map((ba) => ba.amenityId) || []
    );

    // If this amenity is already booked by this booking, it's available
    if (currentBookingAmenityIds.has(amenity.id)) {
      return true;
    }

    const BUFFER_MINUTES = 15;
    const toMinutes = (time: string): number => {
      const clean = time.replace(":", "");
      return parseInt(clean.substring(0, 2), 10) * 60 + parseInt(clean.substring(2, 4), 10);
    };

    const proposedHasBuffers = booking.hasBuffers ?? true;
    const proposedStart = toMinutes(booking.startTime);
    const proposedEnd = endTimeToMinutes(booking.endTime, booking.startTime);
    const proposedBufferStart = proposedHasBuffers ? proposedStart - BUFFER_MINUTES : proposedStart;
    const proposedBufferEnd = proposedHasBuffers ? proposedEnd + BUFFER_MINUTES : proposedEnd;

    // Check if amenity is booked by OTHER bookings during this time (buffer + same-member aware)
    const conflictingBookings = bookings.filter((b) => {
      // Skip cancelled bookings
      if (b.isCancelled) return false;
      // Skip this booking itself
      if (b.id === booking.id) return false;
      // Check if this booking has this amenity
      if (!b.bookedAmenities?.some((ba) => ba.amenityId === amenity.id)) return false;

      const bookingStart = toMinutes(b.startTime);
      const bookingEnd = endTimeToMinutes(b.endTime, b.startTime);
      const bookingHasBuffers = b.hasBuffers ?? true;
      const isSameMember = b.memberId === booking.memberId;
      const bookingBufferStart = bookingHasBuffers ? bookingStart - BUFFER_MINUTES : bookingStart;
      const bookingBufferEnd = bookingHasBuffers ? bookingEnd + BUFFER_MINUTES : bookingEnd;

      // Core time overlap is always a conflict.
      if (proposedStart < bookingEnd && proposedEnd > bookingStart) return true;

      // Same member may book directly adjacent (no buffer needed against their own booking).
      if (isSameMember) {
        const endsBeforeExisting = proposedEnd > bookingBufferStart && proposedEnd <= bookingStart;
        const startsAfterExisting = proposedStart >= bookingEnd && proposedStart < bookingBufferEnd;
        if (endsBeforeExisting || startsAfterExisting) return false;
      }

      // Cross-member (or same-member non-adjacent): a buffer overlap is a conflict.
      return proposedBufferStart < bookingBufferEnd && proposedBufferEnd > bookingBufferStart;
    });

    return conflictingBookings.length === 0;
  };

  // Check if amenity is excluded for the selected room
  const isAmenityExcluded = (amenity: Amenity): boolean => {
    if (!booking?.roomId || !amenity.exclusions) return false;
    return amenity.exclusions.includes(booking.roomId);
  };

  // Handle amenity selection toggle
  const toggleAmenity = (amenityId: string) => {
    if (selectedAmenityIds.includes(amenityId)) {
      setSelectedAmenityIds(selectedAmenityIds.filter((id) => id !== amenityId));
    } else {
      setSelectedAmenityIds([...selectedAmenityIds, amenityId]);
    }
  };

  // Check if selection has changed
  const hasChanges = useMemo(() => {
    const currentAmenityIds = booking?.bookedAmenities?.map((ba) => ba.amenityId) || [];
    if (currentAmenityIds.length !== selectedAmenityIds.length) return true;
    return !currentAmenityIds.every((id) => selectedAmenityIds.includes(id));
  }, [booking?.bookedAmenities, selectedAmenityIds]);

  const handleSubmit = async () => {
    if (!booking) return;

    try {
      await updateAmenitiesMutation.mutateAsync({
        bookingId: booking.id,
        amenityIds: selectedAmenityIds,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      // Error is handled by the mutation's onError
    }
  };

  const renderAmenityList = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
          <span className="ml-2 text-gray-600">Checking availability...</span>
        </div>
      );
    }

    if (bookableAmenities.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>No bookable amenities available at this location.</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {bookableAmenities.map((amenity) => {
          const excluded = isAmenityExcluded(amenity);
          const available = !excluded && isAmenityAvailable(amenity);
          const isSelected = selectedAmenityIds.includes(amenity.id);
          const isDisabled = excluded || !available;

          return (
            <div
              key={amenity.id}
              className={`flex items-center space-x-3 p-3 rounded-lg border ${
                isDisabled
                  ? "bg-gray-100 border-gray-200 opacity-60"
                  : isSelected
                    ? "bg-indigo-50 border-indigo-200"
                    : "bg-white border-gray-200 hover:border-indigo-300"
              }`}
            >
              <Checkbox
                id={`amenity-${amenity.id}`}
                checked={isSelected}
                disabled={isDisabled}
                onCheckedChange={() => toggleAmenity(amenity.id)}
              />
              <Label
                htmlFor={`amenity-${amenity.id}`}
                className={`flex-1 cursor-pointer ${isDisabled ? "cursor-not-allowed" : ""}`}
              >
                <AmenityIconRenderer amenity={amenity} withDescription />
                {excluded && (
                  <span className="block text-xs text-red-600 mt-1">Not available for this room</span>
                )}
                {!excluded && !available && (
                  <span className="block text-xs text-amber-600 mt-1">Booked during this time</span>
                )}
              </Label>
            </div>
          );
        })}
      </div>
    );
  };

  if (!booking) return null;

  const currentAmenityCount = booking.bookedAmenities?.length || 0;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="size-5" />
            Manage Amenities
          </DialogTitle>
          <DialogDescription>
            {currentAmenityCount > 0
              ? `This booking has ${currentAmenityCount} amenity${currentAmenityCount > 1 ? "ies" : ""} booked. Select or deselect amenities below.`
              : "Add bookable amenities to this booking."}
            {isSeries && (
              <span className="block mt-1">
                This is a repeating booking: changes apply to every date, and availability is checked against every
                future date when you save.
              </span>
            )}
            {customisedCount > 0 && (
              <span className="block mt-1">
                {customisedCount} future date{customisedCount === 1 ? " has its" : "s have their"} own amenities and won't change.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] pr-4">
          {renderAmenityList()}
        </ScrollArea>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateAmenitiesMutation.isPending}
            className={secondaryButtonClasses}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={updateAmenitiesMutation.isPending || !hasChanges}
            className={defaultButtonClasses}
          >
            {updateAmenitiesMutation.isPending ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              "Update Amenities"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
